import { Link } from '@backstage/core-components';
import { makeStyles } from '@material-ui/core';
import type { ComponentType, ReactNode } from 'react';

const useStyles = makeStyles({
  root: {
    borderRadius: 'var(--bui-radius-4)',
    background: 'var(--bui-white)',
    boxShadow: 'var(--hone-portal-elevation-card)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '14px 20px',
    borderBottom: '1px solid var(--hone-portal-hairline-soft)',
  },
  title: {
    flex: 1,
    fontSize: 13.5,
    fontWeight: 600,
    color: 'var(--bui-fg-primary)',
  },
  action: {
    fontSize: 12,
    fontWeight: 500,
  },
  icon: {
    width: 14,
    height: 14,
    opacity: 0.5,
    display: 'block',
  },
  // Rows are laid out by each panel; the last one drops its rule so no hairline
  // lands against the card's rounded bottom edge.
  body: {
    '& > *:last-child': { borderBottom: 'none' },
  },
  empty: {
    padding: '16px 20px',
    fontSize: 12.5,
    color: 'var(--bui-fg-secondary)',
  },
});

export interface HomePanelProps {
  title: string;
  /** Blue text link in the header, e.g. "View all ▸". */
  action?: { label: string; to: string };
  /** Header glyph, used where a panel has no action link (mock 1c: Starred). */
  icon?: ComponentType<{ className?: string }>;
  /** Rendered in place of the rows when the panel has nothing to show. */
  emptyText?: string;
  isEmpty?: boolean;
  /** Shows a resting placeholder instead of an empty body while data loads. */
  loading?: boolean;
  children: ReactNode;
}

// The card shell shared by Recently viewed / Starred / Announcements (mock 1c):
// hairline-ruled header over a flush list, card elevation, 12px radius.
export function HomePanel({
  title,
  action,
  icon: Icon,
  emptyText,
  isEmpty,
  loading,
  children,
}: HomePanelProps) {
  const classes = useStyles();

  const body = () => {
    if (loading) {
      return <div className={classes.empty}>Loading…</div>;
    }
    if (isEmpty) {
      return <div className={classes.empty}>{emptyText}</div>;
    }
    return <div className={classes.body}>{children}</div>;
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <span className={classes.title}>{title}</span>
        {action && (
          <Link to={action.to} underline="none" className={classes.action}>
            {action.label} ▸
          </Link>
        )}
        {!action && Icon && <Icon className={classes.icon} />}
      </div>
      {body()}
    </div>
  );
}
